import Phaser from 'phaser';
import { tr } from '../i18n';
import { DEPTH, UI } from './theme';
import { talentsSummary } from '../talents';
import type Player from '../objects/Player';

export interface HudState {
  score: number;
  floor: number;
  player: Player;
  doorUnlocked: boolean;
  remainingEnemies: number;
  highScore: number;
  highFloor: number;
}

const FONT = { fontSize: '20px', fontFamily: 'monospace' } as const;

/** 左上角 HUD（得分/楼层/HP/剩余敌人/天赋/自动射击/最高记录） */
export default class Hud {
  private scoreText: Phaser.GameObjects.Text;
  private floorText: Phaser.GameObjects.Text;
  private hpText: Phaser.GameObjects.Text;
  private killText: Phaser.GameObjects.Text;
  private talentText: Phaser.GameObjects.Text;
  private autoFireText: Phaser.GameObjects.Text;
  private bestText: Phaser.GameObjects.Text;

  constructor(private readonly scene: Phaser.Scene) {
    const mk = (y: number, text: string, color: string, opts = {}) =>
      scene.add
        .text(16, y, text, { ...FONT, color, ...opts })
        .setScrollFactor(0)
        .setDepth(DEPTH.hud);

    this.scoreText = mk(16, tr('hud.score', { v: 0 }), UI.gold);
    this.floorText = mk(40, tr('hud.floor', { v: 1 }), UI.blue);
    this.hpText = mk(64, tr('hud.hp', { cur: 100, max: 100 }), UI.red);
    this.killText = mk(88, tr('hud.remaining', { n: 0 }), UI.orange);
    this.talentText = mk(114, '', UI.purple, {
      fontSize: '14px',
      wordWrap: { width: 620 },
    });
    this.autoFireText = mk(158, tr('hud.autoFire', { state: tr('common.off') }), UI.green, {
      fontSize: '16px',
    });
    this.bestText = mk(180, '', UI.muted, { fontSize: '14px' });
  }

  refresh(s: HudState) {
    this.scoreText.setText(tr('hud.score', { v: s.score }));
    this.floorText.setText(tr('hud.floor', { v: s.floor }));
    this.hpText.setText(
      tr('hud.hp', { cur: Math.max(0, Math.ceil(s.player.hp)), max: s.player.maxHp })
    );
    this.killText.setText(
      s.doorUnlocked ? tr('hud.doorOpen') : tr('hud.remaining', { n: s.remainingEnemies })
    );
    this.talentText.setText(talentsSummary(s.player.talents, s.floor));
    this.bestText.setText(tr('hud.best', { score: s.highScore, floor: s.highFloor }));
  }

  setAutoFire(on: boolean) {
    this.autoFireText.setText(
      tr('hud.autoFire', { state: tr(on ? 'common.on' : 'common.off') })
    );
  }
}
